import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  Ellipsis,
  ExternalLink,
  Globe,
  Plus,
  RotateCcw,
  Search,
  SearchX,
  Settings,
  Trash2,
  TriangleAlert,
} from 'lucide-react';
import { useCallback, useState } from 'react';

import { RouterAnchor } from '@/app/layout/RouterAnchor';
import type { SiteBody, SitesListParams } from '@/shared/api/generated/model';
import { sitesDelete, sitesList, sitesRestore } from '@/shared/api/generated/sites/sites';
import { tenantsMine } from '@/shared/api/generated/tenants/tenants';
import { ApiError } from '@/shared/api/http';
import { t } from '@/shared/i18n';
import { cx } from '@/shared/lib/cx';
import { useDebounced } from '@/shared/lib/useDebounced';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { ConfirmDialog } from '@/shared/ui/Dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/DropdownMenu';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Input } from '@/shared/ui/Input';
import { Skeleton } from '@/shared/ui/Skeleton';
import { toast } from '@/shared/ui/Toast';

import { SiteFormDialog } from './SiteFormDialog';
import styles from './SitesPage.module.scss';

/** Все тенанты в переключателе — их немного. */
const ALL_TENANTS = '';

const PAGE_SIZE = 24;

/**
 * Все сайты — карточками, а не таблицей.
 *
 * Сайтов у Давида — десятки, а не тысячи, и у каждого важны два-три факта:
 * как называется, подключён ли к адресу, где лежит. Карточка показывает их
 * сразу и целиком ведёт в сайт; таблица заставляла читать строку и искать
 * действие в меню (docs/research/07-navigation-and-home.md).
 *
 * Владелец платформы видит сайты всех тенантов. Остальным сервер сам
 * ограничивает выдачу их тенантом — в браузере это не проверяется: такая
 * проверка защищала бы только от случайного нажатия, а не от запроса,
 * отправленного руками.
 */
export function SitesPage() {
  const queryClient = useQueryClient();

  const [tenantID, setTenantID] = useState(ALL_TENANTS);
  const [search, setSearch] = useState('');
  const [deleted, setDeleted] = useState(false);

  const [creating, setCreating] = useState(false);
  const [removing, setRemoving] = useState<SiteBody>();

  const debouncedSearch = useDebounced(search);

  // «Мои тенанты», а не общий список: общий доступен только владельцу
  // платформы, и форма создания сайта была бы пустой у всех остальных.
  const tenants = useQuery({
    queryKey: ['tenants', 'mine'],
    queryFn: async () => {
      const response = await tenantsMine();

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на запрос тенантов');
      }

      return response.data.items;
    },
  });

  const params: SitesListParams = {
    search: debouncedSearch,
    ...(tenantID !== ALL_TENANTS && { tenant_id: tenantID }),
    ...(deleted && { deleted: true }),
    sort: 'name',
    order: 'asc',
    per_page: PAGE_SIZE,
  };

  // Порциями с «Показать ещё», а не страницами: карточки читают сверху
  // вниз, и листание назад-вперёд здесь только мешало бы.
  const query = useInfiniteQuery({
    queryKey: ['sites', 'cards', params],
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const response = await sitesList({ ...params, page: pageParam });

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на запрос сайтов');
      }

      return response.data;
    },
    getNextPageParam: (last) =>
      last.page * last.per_page < last.total ? last.page + 1 : undefined,
  });

  const sites = query.data?.pages.flatMap((page) => page.items ?? []) ?? [];

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['sites'] });
    // Счётчик сайтов — в карточке тенанта, а список сайтов — в переключателе.
    void queryClient.invalidateQueries({ queryKey: ['tenants'] });
    void queryClient.invalidateQueries({ queryKey: ['switcher'] });
  }, [queryClient]);

  const restore = useCallback(
    (site: SiteBody) => {
      sitesRestore(site.id)
        .then(() => {
          toast.success(t.sites.restored(site.name));
          refresh();
        })
        .catch((cause: unknown) => {
          toast.error(t.sites.restoreFailed, { description: describe(cause) });
        });
    },
    [refresh],
  );

  const remove = useMutation({
    mutationFn: async (site: SiteBody) => {
      await sitesDelete(site.id);
    },
    onSuccess: (_data, site) => {
      toast.success(t.sites.removed(site.name), {
        action: {
          label: t.sites.undo,
          altText: t.sites.undoHint,
          onClick: () => {
            restore(site);
          },
        },
      });
      refresh();
    },
  });

  // Тенант показывается, только когда их у человека несколько: одному
  // клиенту и подпись, и фильтр говорят одно и то же на каждой карточке.
  const manyTenants = (tenants.data ?? []).length > 1;
  const currentTenant = tenants.data?.find((item) => item.id === tenantID);
  const narrowed = debouncedSearch !== '' || tenantID !== ALL_TENANTS;

  return (
    <div className={styles['page']}>
      <header className={styles['head']}>
        <div>
          <h1 className={styles['title']}>{t.sites.title}</h1>
          <p className={styles['subtitle']}>{t.sites.subtitle}</p>
        </div>

        <Button
          iconLeft={<Plus size={15} />}
          onClick={() => {
            setCreating(true);
          }}
        >
          {t.sites.create}
        </Button>
      </header>

      <div className={styles['toolbar']}>
        <Input
          className={styles['toolbar__search']}
          placeholder={t.sites.search}
          aria-label={t.sites.search}
          prefix={<Search size={15} />}
          clearable
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
          }}
        />

        {manyTenants && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" tone="neutral">
                {currentTenant?.name ?? t.sites.allTenants}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuLabel>{t.sites.tenant}</DropdownMenuLabel>
              <DropdownMenuRadioGroup value={tenantID} onValueChange={setTenantID}>
                <DropdownMenuRadioItem value={ALL_TENANTS}>
                  {t.sites.allTenants}
                </DropdownMenuRadioItem>
                {(tenants.data ?? []).map((tenant) => (
                  <DropdownMenuRadioItem key={tenant.id} value={tenant.id}>
                    {tenant.name}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        <Button
          variant={deleted ? 'solid' : 'outline'}
          tone={deleted ? 'accent' : 'neutral'}
          iconLeft={<Trash2 size={15} />}
          aria-pressed={deleted}
          onClick={() => {
            setDeleted((prev) => !prev);
          }}
        >
          {t.sites.trash}
        </Button>
      </div>

      {query.isLoading ? (
        <div className={styles['grid']}>
          {Array.from({ length: 6 }, (_, index) => (
            <Skeleton key={index} height={132} />
          ))}
        </div>
      ) : query.isError ? (
        <EmptyState
          icon={<TriangleAlert />}
          title={t.sites.loadFailed}
          description={t.sites.loadFailedHint}
          action={
            <Button
              variant="outline"
              tone="neutral"
              onClick={() => {
                void query.refetch();
              }}
            >
              {t.sites.retry}
            </Button>
          }
        />
      ) : sites.length === 0 ? (
        deleted ? (
          <EmptyState icon={<Trash2 />} title={t.sites.trashEmpty} />
        ) : narrowed ? (
          <EmptyState
            icon={<SearchX />}
            title={t.sites.notFoundTitle}
            description={t.sites.notFoundText}
            action={
              <Button
                variant="outline"
                tone="neutral"
                onClick={() => {
                  setSearch('');
                  setTenantID(ALL_TENANTS);
                }}
              >
                {t.sites.reset}
              </Button>
            }
          />
        ) : (
          <EmptyState
            icon={<Globe />}
            title={t.sites.emptyTitle}
            description={t.sites.emptyText}
            action={
              <Button
                iconLeft={<Plus size={15} />}
                onClick={() => {
                  setCreating(true);
                }}
              >
                {t.sites.createFirst}
              </Button>
            }
          />
        )
      ) : (
        <>
          <ul className={styles['grid']}>
            {sites.map((site) => (
              <li key={site.id}>
                <SiteCard
                  site={site}
                  showTenant={manyTenants}
                  onRemove={setRemoving}
                  onRestore={restore}
                />
              </li>
            ))}
          </ul>

          {query.hasNextPage && (
            <div className={styles['more']}>
              <Button
                variant="outline"
                tone="neutral"
                loading={query.isFetchingNextPage}
                onClick={() => {
                  void query.fetchNextPage();
                }}
              >
                {t.sites.more}
              </Button>
            </div>
          )}
        </>
      )}

      <SiteFormDialog
        open={creating}
        onOpenChange={setCreating}
        tenants={tenants.data ?? []}
        defaultTenantID={tenantID}
        onDone={refresh}
      />

      <ConfirmDialog
        open={removing !== undefined}
        onOpenChange={(open) => {
          if (!open) {
            setRemoving(undefined);
          }
        }}
        tone="danger"
        title={t.sites.removeTitle(removing?.name ?? '')}
        description={t.sites.removePlain}
        confirmLabel={t.sites.remove}
        onConfirm={async () => {
          if (removing !== undefined) {
            await remove.mutateAsync(removing);
          }
        }}
      />
    </div>
  );
}

/**
 * Карточка сайта. Вся карточка — ссылка в сайт; меню действий лежит поверх
 * неё отдельной кнопкой, а не внутри ссылки: кнопка внутри ссылки — это
 * два действия на одном щелчке.
 */
function SiteCard({
  site,
  showTenant,
  onRemove,
  onRestore,
}: {
  site: SiteBody;
  showTenant: boolean;
  onRemove: (site: SiteBody) => void;
  onRestore: (site: SiteBody) => void;
}) {
  const navigate = useNavigate();
  const inTrash = site.deleted_at !== undefined && site.deleted_at !== null;
  const home = `/sites/${site.id}/prod`;

  const body = (
    <>
      <span className={styles['card__head']}>
        <span className={styles['card__mark']} aria-hidden="true">
          {site.name.charAt(0).toUpperCase()}
        </span>
        <span className={styles['card__titles']}>
          <span className={styles['card__name']}>{site.name}</span>
          <span className={styles['card__slug']}>{site.slug}</span>
        </span>
      </span>

      <span className={styles['card__meta']}>
        {showTenant && <span>{site.tenant_name}</span>}
        {/* Сайт без адреса — не мелочь, а сайт, который ещё никуда не
            подключён: это видно сразу, а не после открытия настроек. */}
        {site.production_url === undefined ? (
          <Badge size="sm" tone="warning">
            <span title={t.sites.noAddressHint}>{t.sites.noAddress}</span>
          </Badge>
        ) : (
          <span className={styles['card__url']}>{hostOf(site.production_url)}</span>
        )}
        {inTrash && (
          <Badge size="sm" tone="neutral">
            {t.sites.deletedAt}
          </Badge>
        )}
      </span>
    </>
  );

  return (
    <div className={cx(styles['card'], inTrash && styles['card--trash'])}>
      {inTrash ? (
        <div className={styles['card__body']}>{body}</div>
      ) : (
        <RouterAnchor href={home} className={cx(styles['card__body'], styles['card__link'])}>
          {body}
        </RouterAnchor>
      )}

      <div className={styles['card__actions']}>
        {inTrash ? (
          <Button
            size="sm"
            variant="outline"
            tone="neutral"
            iconLeft={<RotateCcw size={14} />}
            onClick={() => {
              onRestore(site);
            }}
          >
            {t.sites.restore}
          </Button>
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                iconOnly
                variant="ghost"
                tone="neutral"
                size="sm"
                aria-label={t.sites.actions(site.name)}
              >
                <Ellipsis size={16} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                icon={<ExternalLink size={15} />}
                onSelect={() => {
                  void navigate({
                    to: '/sites/$siteId/$env',
                    params: { siteId: site.id, env: 'prod' },
                  });
                }}
              >
                {t.sites.open}
              </DropdownMenuItem>
              <DropdownMenuItem
                icon={<Settings size={15} />}
                onSelect={() => {
                  void navigate({
                    to: '/sites/$siteId/$env/settings',
                    params: { siteId: site.id, env: 'prod' },
                  });
                }}
              >
                {t.sites.settings}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                tone="danger"
                icon={<Trash2 size={15} />}
                onSelect={() => {
                  onRemove(site);
                }}
              >
                {t.sites.remove}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
}

function describe(cause: unknown): string {
  return cause instanceof ApiError ? cause.detail : t.sites.unknownError;
}

/** Адрес без схемы — короче и читается как адрес сайта, а не как ссылка. */
function hostOf(url: string): string {
  return url.replace(/^https?:\/\//, '');
}
